import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classes from './Collection.module.scss';
import AddToCollectionButton from '@/components/AddToCollectionButton/AddToCollectionButton';
import ShareResourceDropdown from '@/components/ShareResourceDropdown/ShareResourceDropdown';
import FetchableList from '@/components/Collections/FetchableListItems/FetchableList';
import CategorySidebar from '@/components/Collections/CategorySidebar/CategorySidebar';
import CollectionSearchBar from '@/components/Collections/CollectionSearchBar/CollectionSearchBar';
import CollectionEmptyState from './CollectionEmptyState';
import Resource from '@/utils/api/odas/resource';
import { useCollectionPermissions } from '@/hooks/useCollectionPermissions';
import { useAddCategoryModal } from '@/hooks/useAddCategoryModal';
import { useCollectionSearch } from '@/hooks/useCollectionSearch';
import { useRemoveItemFromCollection } from '@/hooks/useRemoveItemFromCollection';
import { collectionPropType, accountPropType } from '@/propTypes/collection';
import { collectionContext } from '@/context/collection.context';
import t from '@/utils/Language/Collection-translations';

/**
 * Determines if the collection has any items (categories or fetchable list items)
 * @param {Object} collection - The collection object
 * @returns {boolean} - True if the collection has items
 */
const collectionHasItems = (collection) => {
  const hasFetchableItems = collection?.fetchable_list_items?.length > 0;
  const hasCategories = collection?.categories?.length > 0;
  return hasFetchableItems || hasCategories;
};

const CollectionDetails = ({ account, collection, updateCollection, updateCategories, categoriesLoading = false }) => {
  const pageRef = useRef();
  const collectionLoaded = collection?.id != null;
  const showSidebar = categoriesLoading || collectionLoaded;
  const [, setUtilizations] = useState({});
  const { isOwner, isEditor } = useCollectionPermissions(collection, account?.data?.id);
  const addNewCategory = useAddCategoryModal({ collection, updateCategories, t });
  const removeItemFromCollection = useRemoveItemFromCollection({ collection, updateCollection, pageRef });
  const { setSearchText, activeSearchText, hasActiveSearch, doSearch, onClear } = useCollectionSearch();

  const hasAnyItems = useMemo(() => collectionHasItems(collection), [collection]);

  /**
   * Create a collection object with resourceType for sharing
   */
  const collectionWithResourceType = useMemo(
    () => ({
      ...collection,
      resourceType: 'Collection'
    }),
    [collection]
  );

  /**
   * Fetches utilization data for the given resource IDs
   * @param {Array<number>} resourceIds - Array of resource IDs
   */
  const getUtilizationsMembers = (resourceIds) => {
    Resource.getUtilizationByResources(resourceIds)
      .then((response) => {
        const results = response.data || {};
        setUtilizations(results);
      })
      .catch(() => {
        // Silently handle error - utilization data is not critical
        setUtilizations({});
      });
  };

  /**
   * Renders the search bar and action buttons
   */
  const renderSearchBar = () => (
    <Row>
      <Col lg="6" className={classes.searchColumnSpacing}>
        <CollectionSearchBar
          placeholder={t('Search in this collection')}
          inputId="searchInput"
          controlId="formBasicName"
          doSearch={doSearch}
          setSearchText={setSearchText}
          onClear={onClear}
        />
      </Col>
      <Col lg="6" className={classes.flushDesktop}>
        <div className={classes.actionButtonsContainer}>
          <ShareResourceDropdown
            resource={collectionWithResourceType}
            buttonStyles={{
              buttonType: 'secondary',
              buttonShape: 'pill',
              buttonText: t('Share'),
              buttonBorder: false,
              buttonIcon: { style: 'fas', font: 'share-nodes', position: 'left', color: 'white' }
            }}
            additionalClasses={classes.actionBtn}
            inviteType="share_resource"
            inviteValue={`Collection:${collection.id}`}
            inviteIsReusable={true}
            shareData={JSON.stringify({ resource: { id: collection.id, slug: collection.slug, resource_type: 'Collection' } })}
          />
          <AddToCollectionButton
            buttonType="secondary"
            additionalClasses={classes.actionBtn}
            resource={collectionWithResourceType}
            showButtonTextOnMobile={true}
            buttonText={t('Add to another collection')}
            buttonIcon={{ style: 'fas', font: 'plus' }}
          />
        </div>
      </Col>
    </Row>
  );

  /**
   * Renders the list of fetchable resources
   */
  const renderFetchableList = () => (
    <FetchableList
      collection={collection}
      pageRef={pageRef}
      hasActiveSearch={hasActiveSearch}
      isOwner={isOwner}
      isEditor={isEditor}
      activeSearchText={activeSearchText}
      removeItemFromCollection={removeItemFromCollection}
      addNewCategory={addNewCategory}
    />
  );

  /**
   * MAIN RENDER - Empty state
   */
  if (collectionLoaded && !hasAnyItems) {
    return (
      <collectionContext.Provider value={{ refreshCollection: updateCollection }}>
        <Container className={classes.collectionDetails}>
          <CollectionEmptyState addNewCategory={addNewCategory} canEdit={isOwner || isEditor} />
        </Container>
      </collectionContext.Provider>
    );
  }

  /**
   * MAIN RENDER - With content
   */
  return (
    <collectionContext.Provider value={{ refreshCollection: updateCollection }}>
      <Container className={classes.collectionDetails}>
        <Row className="mt-3">
          {showSidebar && (
            <CategorySidebar
              collection={collection}
              t={t}
              categoriesLoading={categoriesLoading}
              isOwner={isOwner}
              isEditor={isEditor}
              addNewCategory={addNewCategory}
            />
          )}
          <Col md={showSidebar ? '9' : '12'} className={showSidebar ? 'mt-3 mt-md-0' : ''}>
            {renderSearchBar()}
            {renderFetchableList()}
          </Col>
        </Row>
      </Container>
    </collectionContext.Provider>
  );
};

CollectionDetails.propTypes = {
  account: accountPropType,
  collection: collectionPropType.isRequired,
  updateCollection: PropTypes.func.isRequired,
  updateCategories: PropTypes.func.isRequired,
  categoriesLoading: PropTypes.bool
};

CollectionDetails.defaultProps = {
  categoriesLoading: false
};

export default CollectionDetails;
